import { Box, Flex, Spinner, useToast } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "@/contexts/proposalFormContext";
import { getProposalFormSteps } from "./getProposalFormSteps";
import ProposalFormStepper, { steps } from "../ProposaFormStepper";
import { useNavigate, useParams } from "react-router-dom";
import { routes } from "../../routes/index";
import {
  getProposalByBalanceSolicitationId,
  updateProposal,
} from "@/services/proposal.service";
import { mergeFormDataWithProposal } from "./mergeFormDataWithProposal";

export default function ProposalForm() {
  const [activeStep, setActiveStep] = useState<number>(0);
  const { formData, updateFormData } = useForm();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const cpfConsultPagePath = routes.find((route) => route.name === "consult-cpf")?.path;

  const loadProposal = useCallback(
    async (proposalId: string) => {
      setLoading(false);
      try {
        const proposal = await getProposalByBalanceSolicitationId(proposalId);
        if (proposal) {
          updateFormData("proposal", proposal);
        } else {
          throw new Error("No proposal data available.");
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch the proposal.";
        setError(errorMessage);
        toast({
          title: "Erro ao carregar proposta",
          description: errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [toast, updateFormData]
  );

  useEffect(() => {
    if (id && loading) {
      loadProposal(id);
    }
  }, [id, loadProposal, loading]);

  const handleNext = useCallback(() => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prev) => prev + 1);
    } else {
      toast({
        title: "Proposta concluída!",
        description: "Sua proposta foi enviada com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [activeStep, toast]);

  const handleBack = useCallback(() => {
    if (activeStep === 0) {
      navigate(cpfConsultPagePath!);
    } else {
      setActiveStep((prev) => prev - 1);
    }
  }, [activeStep, navigate]);

  const handleStepClick = useCallback((step: number) => {
    setActiveStep(step);
  }, []);

  const removeMask = (value?: string) => {
    if (!value) return value;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(value)) {
      return value; 
    }
    return value.replace(/\D/g, "");
  };

  const handleSubmit = useCallback(async () => {
    try {
      if (!formData.proposal) {
        toast({
          title: "Erro na operação",
          description: "Proposta não encontrada.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });

        return;
      }

      formData.personalData.cellphone = removeMask(
        formData.personalData.cellphone
      );
      formData.address.cep = removeMask(formData.address.cep);
      formData.bankData.pixKey = removeMask(formData.bankData.pixKey);

      const newProposal = mergeFormDataWithProposal(
        formData,
      );

      updateFormData("proposal", newProposal);

      const updatedProposal = await updateProposal(newProposal);

      toast({
        title: `Proposta de ${newProposal.NaturalPerson?.name} feita!`,
        description: "Todas as informações foram confirmadas com sucesso.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      const linkPath = routes.find(route => route.name == "proposal-link")?.path
      if(linkPath) navigate(linkPath, { state: { proposal: updatedProposal } })
    } catch(error) {
      console.error(error);
      toast({
        title: "Erro na operação",
        description: "Ocorreu um erro ao processar suas informações.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [formData, updateFormData, toast]);

  const handleAdvance = () => {
    if (activeStep === steps.length - 1) {
      handleSubmit();
    } else {
      handleNext();
    }
  };

  const stepsData = getProposalFormSteps(
    formData,
    updateFormData,
    handleAdvance,
    handleBack,
    handleSubmit
  );
  const currentStep = stepsData[activeStep];

  if (loading)
    return (
      <Flex w="100%" pt="5rem" align="flex-start" justify="center">
        <Spinner size="xl" />
      </Flex>
    );

  if (error) return <Box>{error}</Box>;

  return (
    <Box width="100%">
      <ProposalFormStepper
        activeStep={activeStep}
        onStepClick={handleStepClick}
      />
      {currentStep.component}
    </Box>
  );
}
